/**
 * VHAKACHA COORDINATE READER
 * ============================================================
 * `listings.coordinates` has been written in three shapes:
 *
 *   { latitude, longitude }   what the provider app writes today
 *   [ latitude, longitude ]   the original client array
 *   { lat, lng }              the old in-app seeder
 *
 * The map components indexed the array form directly, so every listing
 * saved through the provider app — which writes the object form — read
 * back `undefined` for both values and handed NaN to Mapbox.
 *
 * `toLatLng()` accepts any of the three, or an entity that carries one
 * under `.coordinates`, and returns a single shape. It returns null when
 * there is nothing usable so callers can skip the marker rather than
 * render one at an undefined position.
 * ============================================================
 */

const finite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * @param {object|Array|null} raw  a coordinate value, or a document holding
 *                                 one under `coordinates`
 * @returns {{latitude: number, longitude: number}|null}
 */
export function toLatLng(raw) {
  if (!raw) return null;

  // Accept either the coordinate itself or the document that carries it.
  const source = raw.coordinates !== undefined ? raw.coordinates : raw;
  if (!source || typeof source !== 'object') return null;

  const latitude = Array.isArray(source)
    ? finite(source[0])
    : finite(source.latitude != null ? source.latitude : source.lat);
  const longitude = Array.isArray(source)
    ? finite(source[1])
    : finite(source.longitude != null ? source.longitude : source.lng);

  if (latitude === null || longitude === null) return null;
  return { latitude, longitude };
}

/** Map viewport for a document, or null when it has no usable position. */
export function toViewState(raw, zoom) {
  const point = toLatLng(raw);
  return point ? { ...point, zoom } : null;
}
